import { useEffect, useState } from 'react';
import { Plus, Star, History } from 'lucide-react';
import { fetchClientHistory, fetchFavorites } from '@/lib/api';
import type { ContextProduct } from '@/lib/api';
import type { GiftProduct } from '@/lib/types';

interface Props {
  clientId: string | null;
  currentPackageId: string | null;
  onAdd: (product: GiftProduct) => void | Promise<void>;
}

export function CustomerContext({ clientId, currentPackageId, onAdd }: Props) {
  const [history, setHistory] = useState<ContextProduct[]>([]);
  const [favorites, setFavorites] = useState<ContextProduct[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    fetchFavorites(6)
      .then(setFavorites)
      .catch(() => setFavorites([]));
  }, []);

  useEffect(() => {
    if (!clientId) {
      setHistory([]);
      return;
    }
    setLoadingHistory(true);
    fetchClientHistory(clientId, currentPackageId ?? undefined)
      .then((items) => setHistory(items.slice(0, 6)))
      .catch(() => setHistory([]))
      .finally(() => setLoadingHistory(false));
  }, [clientId, currentPackageId]);

  return (
    <div className="space-y-4 border-t border-bmade-200 pt-3 dark:border-bmade-800">
      <ContextBlock
        icon={History}
        title="Vorig jaar bij deze klant"
        items={history}
        loading={loadingHistory}
        empty={
          clientId ? 'Nog geen eerder pakket voor deze klant.' : 'Koppel klant om historie te zien.'
        }
        onAdd={onAdd}
      />
      <ContextBlock
        icon={Star}
        title="Jouw favorieten"
        items={favorites}
        loading={false}
        empty="Nog geen favorieten."
        onAdd={onAdd}
      />
    </div>
  );
}

function ContextBlock({
  icon: Icon,
  title,
  items,
  loading,
  empty,
  onAdd,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  items: ContextProduct[];
  loading: boolean;
  empty: string;
  onAdd: (product: GiftProduct) => void | Promise<void>;
}) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-bmade-600 dark:text-bmade-400">
        <Icon className="h-3 w-3" />
        {title}
      </div>
      {loading ? (
        <p className="text-[11px] text-bmade-500">Laden...</p>
      ) : items.length === 0 ? (
        <p className="text-[11px] text-bmade-400">{empty}</p>
      ) : (
        <ul className="space-y-1">
          {items.map(({ product, count }) => (
            <li
              key={product.id}
              className="flex items-center justify-between gap-2 rounded border border-bmade-200 bg-white px-2 py-1 text-[11px] transition-colors hover:border-bmade-300 dark:border-bmade-800 dark:bg-bmade-950/40 dark:hover:border-bmade-700"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-bmade-950 dark:text-white">
                  {product.name}
                </div>
                <div className="truncate text-bmade-500">
                  {product.supplier?.name ?? '—'} · {count}×
                </div>
              </div>
              <button
                type="button"
                onClick={() => onAdd(product)}
                className="shrink-0 rounded p-1 text-accent-600 transition-colors hover:bg-accent-50 dark:text-accent-400 dark:hover:bg-accent-500/10"
                title="Toevoegen aan pakket"
              >
                <Plus className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
